import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Plus, CheckCircle, Loader2 } from 'lucide-react'
import { getPersonalLists, addListItem, createPersonalList } from '../../services/api'
import type { PersonalList } from '../../types'

interface AddToListModalProps {
  isOpen: boolean
  onClose: () => void
  resourceId: string
  resourceType: string
  title: string
}

export default function AddToListModal({
  isOpen,
  onClose,
  resourceId,
  resourceType,
  title,
}: AddToListModalProps) {
  const queryClient = useQueryClient()
  const [selectedListId, setSelectedListId] = useState<string | null>(null)
  const [showCreateNew, setShowCreateNew] = useState(false)
  const [newListName, setNewListName] = useState('')
  const [success, setSuccess] = useState(false)

  const { data: lists = [], isLoading } = useQuery({
    queryKey: ['personalLists'],
    queryFn: getPersonalLists,
    enabled: isOpen,
  })

  const addMutation = useMutation({
    mutationFn: async (listId: string) => {
      await addListItem(listId, resourceId, resourceType, title)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personalLists'] })
      queryClient.invalidateQueries({ queryKey: ['listItems'] })
      setSuccess(true)
      setTimeout(() => {
        onClose()
        setSuccess(false)
        setSelectedListId(null)
      }, 1500)
    },
  })

  const createMutation = useMutation({
    mutationFn: async (name: string) => {
      const list = await createPersonalList(name)
      await addListItem(list.list_id, resourceId, resourceType, title)
      return list
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personalLists'] })
      queryClient.invalidateQueries({ queryKey: ['listItems'] })
      setSuccess(true)
      setTimeout(() => {
        onClose()
        setSuccess(false)
        setSelectedListId(null)
        setNewListName('')
        setShowCreateNew(false)
      }, 1500)
    },
  })

  const handleAdd = () => {
    if (showCreateNew && newListName.trim()) {
      createMutation.mutate(newListName.trim())
    } else if (selectedListId) {
      addMutation.mutate(selectedListId)
    }
  }

  if (!isOpen) return null

  const isPending = addMutation.isPending || createMutation.isPending

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        {success ? (
          <div className="text-center py-6">
            <CheckCircle className="mx-auto h-10 w-10 text-green-500 mb-3" />
            <p className="text-base font-medium text-gray-900">Added to list</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Add to List</h2>
              <button
                onClick={onClose}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-gray-500 mb-4 truncate" title={title}>
              Adding: <span className="font-medium text-gray-700">{title}</span>
            </p>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 text-acad-500 animate-spin" />
              </div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {lists.map((list: PersonalList) => (
                  <label
                    key={list.list_id}
                    className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                      selectedListId === list.list_id && !showCreateNew
                        ? 'border-acad-300 bg-acad-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="listSelection"
                      checked={selectedListId === list.list_id && !showCreateNew}
                      onChange={() => {
                        setSelectedListId(list.list_id)
                        setShowCreateNew(false)
                      }}
                      className="h-4 w-4 text-acad-500 focus:ring-acad-500 border-gray-300"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{list.name}</p>
                      <p className="text-xs text-gray-400">
                        {list.item_count} item{list.item_count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </label>
                ))}

                {/* Create New List option */}
                <label
                  className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                    showCreateNew
                      ? 'border-acad-300 bg-acad-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="listSelection"
                    checked={showCreateNew}
                    onChange={() => {
                      setShowCreateNew(true)
                      setSelectedListId(null)
                    }}
                    className="h-4 w-4 text-acad-500 focus:ring-acad-500 border-gray-300"
                  />
                  <div className="flex items-center gap-1.5">
                    <Plus className="h-4 w-4 text-acad-600" />
                    <span className="text-sm font-medium text-acad-600">Create New List</span>
                  </div>
                </label>

                {showCreateNew && (
                  <div className="pl-10">
                    <input
                      type="text"
                      value={newListName}
                      onChange={(e) => setNewListName(e.target.value)}
                      placeholder="New list name"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-acad-500 focus:ring-1 focus:ring-acad-500 outline-none"
                      autoFocus
                    />
                  </div>
                )}
              </div>
            )}

            {/* Error */}
            {(addMutation.isError || createMutation.isError) && (
              <div className="mt-3 rounded-lg bg-red-50 border border-red-200 p-2 text-xs text-red-700">
                Failed to add resource. Please try again.
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={onClose}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                disabled={
                  isPending ||
                  (!selectedListId && !showCreateNew) ||
                  (showCreateNew && !newListName.trim())
                }
                className="rounded-lg bg-acad-500 px-4 py-2 text-sm font-medium text-white hover:bg-acad-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isPending ? 'Adding...' : 'Add'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
